import React from 'react';
import { FieldError, UseFormRegister } from 'react-hook-form';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

type TextInputProps = {
  id: string;
  name?: string;
  register: UseFormRegister<any>;
  errors: FieldError | undefined;
  srLabel?: string;
  label?: string;
  placeholder: string;
  className?: string;
  inputWrapperClassName?: string;
  makeRounded?: boolean;
  required?: boolean;
  type?: 'text' | 'email' | 'password' | 'date' | 'textarea';
  description?: React.JSX.Element | string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export default function TextInput({
  id,
  name,
  register,
  errors,
  srLabel,
  label,
  placeholder,
  className,
  inputWrapperClassName,
  makeRounded,
  required,
  type = 'text',
  description,
  disabled,
  ...inputProps
}: TextInputProps) {
  return (
    <div className={className}>
      {srLabel && (
        <label htmlFor={id} className="sr-only">
          {srLabel}
        </label>
      )}
      {label && (
        <label
          htmlFor={id}
          className={`${description ? '' : 'mb-[0.5rem]'} block text-[1rem] font-bold text-gray-900`}
        >
          {label} {required && <span className="text-primary-100">*</span>}
        </label>
      )}
      {description && (
        <label htmlFor={id} className="mb-[0.5rem] block">
          {description}
        </label>
      )}
      <div
        className={clsx(
          twMerge(
            'flex w-full items-center gap-2 rounded-xs border bg-white p-3',
            errors ? 'border-red/70' : 'border-neutral-50 focus-within:border-neutral-100',
            makeRounded && 'rounded-full px-5',
            inputWrapperClassName,
          ),
          {
            '!bg-gray-50': disabled,
          },
        )}
      >
        {type === 'textarea' && (
          // @ts-ignore
          <textarea
            {...register(name || id)}
            id={id}
            name={name || id}
            placeholder={placeholder}
            className="w-full placeholder:text-neutral-50 focus:outline-none"
            {...inputProps}
          />
        )}
        {['text', 'email', 'password', 'date'].find((t) => t === type) && (
          <input
            {...register(name || id)}
            id={id}
            type={type}
            name={name || id}
            placeholder={placeholder}
            className="w-full bg-inherit placeholder:text-neutral-50 focus:outline-none"
            disabled={disabled}
            {...inputProps}
          />
        )}
        {errors && <ExclamationCircleIcon className=" h-5 w-auto text-red" />}
      </div>
      {errors && <p className="text-sm text-red">{errors.message}</p>}
    </div>
  );
}
